import { useState, type FormEvent } from 'react';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { aboutData } from '@/mocks/about';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  useScrollToTop();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = fd.get('phone_alt') as string;
    if (honeypot && honeypot.trim().length > 0) {
      setFormStatus('success');
      setFormMessage('消息已发送！我们会尽快回复你。');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    fd.delete('phone_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9slrk5lbrt1qcgaj5rg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送！我们会尽快回复你。');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '发送失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '蜜桃网站是什么时候成立的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃网站创立于2020年，是专为年轻用户打造的甜蜜生活内容平台，目前拥有超过50万活跃用户。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在蜜桃网站投稿？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过"联系我们"页面提交投稿意向，我们的编辑团队会在3个工作日内回复你。',
        },
      },
      {
        '@type': 'Question',
        name: '蜜桃网站的内容团队有哪些背景？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃网站的内容团队由来自时尚、美食、心理学、旅行等不同领域的80多位专业创作者组成，每月产出超过200篇优质原创内容。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="mb-12 md:mb-16">
              <div className="text-center mb-8 md:mb-10">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
                  {aboutData.brandIntro.title}
                </h1>
                <p className="text-sm md:text-base text-primary-500 font-medium">
                  {aboutData.brandIntro.subtitle}
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                {aboutData.brandIntro.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-4 md:p-6 bg-background-50 border border-background-200/60 rounded-lg"
                  >
                    <p className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</p>
                    <p className="text-xs md:text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  {aboutData.brandIntro.content.map((paragraph, i) => (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20modern%20creative%20office%20space%20with%20young%20team%20collaborating%20on%20laptops%20natural%20light%20peach%20cream%20tones%20minimalist%20interior%20design%20plants%20friendly%20atmosphere&width=800&height=500&seq=about-brand-01&orientation=landscape"
                    alt="蜜桃网站团队"
                    className="w-full h-[280px] md:h-[360px] object-cover object-top"
                  />
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14 border-t border-background-200/60">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                  {aboutData.reputation.title}
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  {aboutData.reputation.content.map((paragraph, i) => (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">品牌荣誉</h3>
                  <ul className="space-y-3">
                    {aboutData.reputation.awards.map((award, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-accent-100 text-accent-500 mt-0.5">
                          <i className="ri-medal-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{award}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <section id="contact" className="py-10 md:py-14 border-t border-background-200/60">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                  {aboutData.contact.title}
                </h2>
                <p className="text-sm text-foreground-600">{aboutData.contact.description}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">邮箱</p>
                        <p className="text-sm text-foreground-800">{aboutData.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-map-pin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">地址</p>
                        <p className="text-sm text-foreground-800">{aboutData.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                        <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电话</p>
                        <p className="text-sm text-foreground-800">{aboutData.contact.phone}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-3">关注我们</p>
                    <div className="flex gap-3 flex-wrap">
                      {aboutData.contact.social.map((s) => (
                        <a
                          key={s.name}
                          href={s.url}
                          className="flex items-center gap-2 px-4 py-2 bg-background-100 text-foreground-600 rounded-full text-sm hover:text-primary-500 hover:bg-primary-50 transition-colors whitespace-nowrap"
                        >
                          <i className={`${s.icon} w-4 h-4 flex items-center justify-center`}></i>
                          {s.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div className="honeypot-wrap">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div>
                      <label htmlFor="name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm text-foreground-700 mb-1">消息</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 text-background-50 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-primary-500 text-center mt-2">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center mt-2">{formMessage}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}